import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import axiosInstance from '../../services/api';
import { INK, FONTS } from '../../styles/ledger';
import { SectionHeader, StatusDot } from '../../components/admin/LedgerPrimitives';

interface AiHealthData {
  request_count_24h: number;
  error_rate_24h: number;
  p50_latency_ms: number;
  p95_latency_ms: number;
  circuit_breaker: 'open' | 'closed';
  retry_after_seconds: number;
  hourly_usd_spend: number;
  stuck_pending_count: number;
  refund_failed_count: number;
}

const POLL_INTERVAL_MS = 60_000;

const AiHealthWidget: React.FC = () => {
  const [health, setHealth] = useState<AiHealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchHealth = async () => {
    try {
      const res = await axiosInstance.get<AiHealthData>('/admin/ai/health');
      setHealth(res.data);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    const timer = setInterval(fetchHealth, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  const breakerOpen = health?.circuit_breaker === 'open';
  const stuckAmber = (health?.stuck_pending_count ?? 0) > 0;
  const refundRed = (health?.refund_failed_count ?? 0) > 0;
  const hourlySpend = health?.hourly_usd_spend ?? 0;
  const spendTone = hourlySpend > 40 ? INK.inactive : hourlySpend > 5 ? INK.warning : INK.accent;

  const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.75, borderBottom: `1px solid ${INK.hairlineSoft}`, '&:last-of-type': { borderBottom: 'none' } }}>
      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10.5, color: INK.subtle, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
        {label}
      </Typography>
      {children}
    </Stack>
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <SectionHeader eyebrow="AI agent" title="System health" description="Polled every minute." />
        {loading && <CircularProgress size={14} sx={{ color: INK.muted, mt: 1 }} />}
        {error && (
          <Tooltip title="Could not fetch AI health data">
            <ErrorIcon sx={{ fontSize: 18, color: INK.inactive, mt: 1 }} />
          </Tooltip>
        )}
      </Stack>

      {health && (
        <Stack>
          <Row label="Circuit breaker">
            <StatusDot label={breakerOpen ? 'Open' : 'Closed'} tone={breakerOpen ? 'inactive' : 'active'} />
          </Row>
          <Row label="Stuck charges">
            {stuckAmber ? (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <WarningIcon sx={{ fontSize: 14, color: INK.warning }} />
                <Typography sx={{ fontFamily: FONTS.mono, fontSize: 13, color: INK.warning, fontWeight: 600 }}>
                  {health.stuck_pending_count}
                </Typography>
              </Stack>
            ) : (
              <Typography sx={{ fontFamily: FONTS.mono, fontSize: 13, color: INK.accent, fontWeight: 600 }}>00</Typography>
            )}
          </Row>
          <Row label="Refund failed">
            {refundRed ? (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ErrorIcon sx={{ fontSize: 14, color: INK.inactive }} />
                <Typography sx={{ fontFamily: FONTS.mono, fontSize: 13, color: INK.inactive, fontWeight: 600 }}>
                  {health.refund_failed_count}
                </Typography>
              </Stack>
            ) : (
              <Typography sx={{ fontFamily: FONTS.mono, fontSize: 13, color: INK.accent, fontWeight: 600 }}>00</Typography>
            )}
          </Row>
          <Row label="Hourly spend">
            <Typography sx={{ fontFamily: FONTS.mono, fontSize: 13, color: spendTone, fontWeight: 600 }}>
              ${hourlySpend.toFixed(4)}
            </Typography>
          </Row>
          <Row label="24h requests">
            <Typography sx={{ fontFamily: FONTS.mono, fontSize: 13, color: INK.text, fontWeight: 600 }}>
              {health.request_count_24h.toLocaleString()}
            </Typography>
          </Row>
          <Box sx={{ pt: 1.5, textAlign: 'right' }}>
            <Typography
              component={Link}
              to="/admin/ai/conversations"
              sx={{
                fontFamily: FONTS.mono,
                fontSize: 10.5,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: INK.accent,
                fontWeight: 500,
                textDecoration: 'none',
                '&:hover': { color: INK.accentHover },
              }}
            >
              View conversations →
            </Typography>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default AiHealthWidget;
